'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Header from '@/components/common/Header';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { useTranslation } from '@/hooks/useTranslation';
import { createClient } from '@/lib/supabase/client';

interface Manufacturer {
  id: string;
  company_name: string;
  logo_url: string | null;
  city: string;
  country: string;
  description: string | null;
  specializations: string[] | null;
  certifications: string[] | null;
  established_year: number | null;
  employee_count: string | null;
  is_audited: boolean;
  products?: Array<{
    id: string;
    name: string;
    price: number | null;
    images: string[] | null;
  }>;
}

export default function ManufacturerDirectoryPage() {
  const { t } = useTranslation();
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [userRole, setUserRole] = useState<string | null>(null);
  const [deletingProductId, setDeletingProductId] = useState<string | null>(null);
  const [stats, setStats] = useState({
    total: 0,
    audited: 0,
    products: 0,
    inquiries: 0
  });

  useEffect(() => {
    fetchManufacturers();
    fetchStats();
  }, []);

  const fetchManufacturers = async () => {
    const supabase = createClient();

    // Check user role
    const { data: { user } } = await supabase.auth.getUser();
    setUserRole(user?.user_metadata?.role || null);

    const { data, error } = await supabase
      .from('manufacturers')
      .select(`
        *,
        products(
          id,
          name,
          price,
          images
        )
      `)
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching manufacturers:', error);
    } else if (data) {
      setManufacturers(data);
    }
    setLoading(false);
  };

  const fetchStats = async () => {
    const supabase = createClient();

    const { count: totalCount } = await supabase
      .from('manufacturers')
      .select('*', { count: 'exact', head: true })
      .eq('is_active', true);

    const { count: auditedCount } = await supabase
      .from('manufacturers')
      .select('*', { count: 'exact', head: true })
      .eq('is_active', true)
      .eq('is_audited', true);

    const { count: productsCount } = await supabase
      .from('products')
      .select('*', { count: 'exact', head: true })
      .eq('is_active', true);

    const { count: inquiriesCount } = await supabase
      .from('inquiries')
      .select('*', { count: 'exact', head: true });

    setStats({
      total: totalCount || 0,
      audited: auditedCount || 0,
      products: productsCount || 0,
      inquiries: inquiriesCount || 0
    });
  };

  const handleDeleteProduct = async (productId: string, productName: string, manufacturerId: string) => {
    if (deletingProductId) return;

    if (!confirm(`Are you sure you want to delete "${productName}"? This action cannot be undone.`)) {
      return;
    }

    setDeletingProductId(productId);

    try {
      const supabase = createClient();
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', productId);

      if (error) throw error;

      // Update local state to remove the product
      setManufacturers(prev => prev.map(m => {
        if (m.id === manufacturerId && m.products) {
          return {
            ...m,
            products: m.products.filter(p => p.id !== productId)
          };
        }
        return m;
      }));

      alert('Product deleted successfully!');
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Failed to delete product. Please try again.');
    } finally {
      setDeletingProductId(null);
    }
  };

  const categories = [
    'All Categories',
    'Yarn & Spinning',
    'Weaving & Knitting',
    'Dyeing & Finishing',
    'Garments & Apparel Manufacturing',
    'Home Textiles',
    'Technical & Industrial Textiles',
    'Accessories & Trims',
    'Packaging & Textile Printing'
  ];

  const locations = [
    'All Locations',
    'Cairo',
    'Alexandria',
    'Giza',
    'Suez',
    '10th of Ramadan City',
    'Obour City'
  ];

  const filteredManufacturers = manufacturers.filter(manufacturer => {
    const matchesSearch =
      manufacturer.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (manufacturer.description && manufacturer.description.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesCategory = selectedCategory === 'all' ||
      (manufacturer.specializations && manufacturer.specializations.includes(selectedCategory));

    const matchesLocation = selectedLocation === 'all' ||
      manufacturer.city === selectedLocation;

    return matchesSearch && matchesCategory && matchesLocation;
  });

  const statsDisplay = [
    { label: 'Total Manufacturers', value: `${stats.total}`, icon: 'BuildingOfficeIcon' },
    { label: 'Audited Companies', value: `${stats.audited}`, icon: 'ShieldCheckIcon' },
    { label: 'Active Products', value: `${stats.products}`, icon: 'CubeIcon' },
    { label: 'Monthly Inquiries', value: `${stats.inquiries}`, icon: 'ChatBubbleLeftRightIcon' }
  ];

  if (loading) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-background pt-16 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading manufacturers...</p>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background pt-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-card to-background border-b border-border">
          <div className="max-w-7xl mx-auto px-6 py-12">
            <div className="text-center mb-10">
              <h1 className="text-4xl md:text-5xl font-headline font-bold text-foreground mb-4">
                {t('manufacturerDirectory')}
              </h1>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                {t('manufacturerDirectoryDesc')}
              </p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {statsDisplay.map((stat, index) => (
                <div key={index} className="bg-card border border-border rounded-xl p-5 hover:shadow-md transition-shadow duration-200">
                  <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg mb-3 mx-auto">
                    <Icon name={stat.icon as any} size={24} variant="outline" className="text-primary" />
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-foreground mb-1">{stat.value}</div>
                    <div className="text-sm text-muted-foreground">{stat.label}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <Icon
                  name="MagnifyingGlassIcon"
                  size={20}
                  variant="outline"
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <input
                  type="text"
                  placeholder={t('searchManufacturersPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3.5 bg-card border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-foreground placeholder:text-muted-foreground"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Manufacturer Registration CTA */}
        <section className="bg-gradient-to-r from-conversion/10 via-conversion/5 to-conversion/10 border-y border-conversion/20">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-conversion/20 rounded-full flex items-center justify-center">
                  <Icon name="BuildingOfficeIcon" size={24} variant="outline" className="text-conversion" />
                </div>
                <div>
                  <h3 className="text-lg font-headline font-bold text-foreground mb-1">
                    {t('areYouManufacturer')}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t('registerFactoryDesc')}
                  </p>
                </div>
              </div>
              <a
                href="/manufacturer-onboarding"
                className="flex items-center gap-2 px-6 py-3 bg-conversion text-conversion-foreground rounded-lg font-semibold hover:bg-conversion/90 transition-all duration-200 shadow-md hover:shadow-lg whitespace-nowrap"
              >
                <Icon name="PlusCircleIcon" size={20} variant="outline" />
                <span>{t('registerYourFactory')}</span>
              </a>
            </div>
          </div>
        </section>

        {/* Filters and Results */}
        <section className="max-w-7xl mx-auto px-6 py-10">
          {/* Filters */}
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">
                {t('category')}
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-4 py-3 bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
              >
                {categories.map((cat, index) => (
                  <option key={index} value={index === 0 ? 'all' : cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">
                {t('location')}
              </label>
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="w-full px-4 py-3 bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
              >
                {locations.map((loc, index) => (
                  <option key={index} value={index === 0 ? 'all' : loc}>
                    {loc}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Results Count */}
          <div className="mb-6">
            <p className="text-muted-foreground">
              {t('showing')} <span className="font-semibold text-foreground">{filteredManufacturers.length}</span> {filteredManufacturers.length === 1 ? t('manufacturer') : t('manufacturers_plural')}
            </p>
          </div>

          {/* Manufacturer Cards Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredManufacturers.map((manufacturer) => (
              <article
                key={manufacturer.id}
                className="bg-card border border-border rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-200 hover:border-primary/50 group"
              >
                {/* Manufacturer Image/Logo */}
                <div className="relative h-48 overflow-hidden bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center">
                  {manufacturer.logo_url ? (
                    <AppImage
                      src={manufacturer.logo_url}
                      alt={manufacturer.company_name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="text-6xl">🏭</div>
                  )}
                  {manufacturer.is_audited && (
                    <div className="absolute top-3 right-3 bg-verified text-verified-foreground px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                      <Icon name="CheckBadgeIcon" size={14} variant="solid" />
                      Audited
                    </div>
                  )}
                </div>

                {/* Card Content */}
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-headline font-semibold text-foreground mb-1 truncate group-hover:text-primary transition-colors">
                        {manufacturer.company_name}
                      </h3>
                      <div className="flex items-center space-x-1.5 text-sm text-muted-foreground">
                        <Icon name="MapPinIcon" size={14} variant="outline" />
                        <span>{manufacturer.city}, {manufacturer.country}</span>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {manufacturer.description || 'No description available'}
                  </p>

                  {/* Specializations */}
                  {manufacturer.specializations && manufacturer.specializations.length > 0 && (
                    <div className="mb-4">
                      <div className="flex flex-wrap gap-1.5">
                        {manufacturer.specializations.slice(0, 2).map((spec, index) => (
                          <span key={index} className="px-3 py-1 bg-primary/10 text-primary text-xs font-semibold rounded-full">
                            {spec}
                          </span>
                        ))}
                        {manufacturer.specializations.length > 2 && (
                          <span className="px-3 py-1 bg-muted text-muted-foreground text-xs rounded-full">
                            +{manufacturer.specializations.length - 2}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-2 mb-4 py-3 border-y border-border">
                    <div>
                      <div className="text-xs text-muted-foreground mb-0.5">Since</div>
                      <div className="text-base font-bold text-foreground">
                        {manufacturer.established_year || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground mb-0.5">Staff</div>
                      <div className="text-base font-bold text-foreground">
                        {manufacturer.employee_count || 'N/A'}
                      </div>
                    </div>
                  </div>

                  {/* Certifications */}
                  {manufacturer.certifications && manufacturer.certifications.length > 0 && (
                    <div className="mb-4 p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center space-x-1.5 mb-2">
                        <Icon name="ShieldCheckIcon" size={14} variant="outline" className="text-muted-foreground" />
                        <span className="text-xs font-semibold text-muted-foreground">Certifications</span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {manufacturer.certifications.slice(0, 2).join(', ')}
                        {manufacturer.certifications.length > 2 && ` +${manufacturer.certifications.length - 2}`}
                      </div>
                    </div>
                  )}

                  {/* Products Section */}
                  {manufacturer.products && manufacturer.products.length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold text-foreground">Products ({manufacturer.products.length})</h4>
                      </div>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {manufacturer.products.map((product) => (
                          <div key={product.id} className="p-2 bg-muted/30 rounded-lg border border-border hover:border-primary/50 transition-colors">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-foreground truncate">{product.name}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  {product.price ? `EGP ${(product.price * 50).toLocaleString('en-US', { minimumFractionDigits: 2 })}/unit` : 'Contact for price'}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* CTA Button */}
                  <Link
                    href={`/manufacturer/${manufacturer.id}`}
                    className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors duration-200 flex items-center justify-center space-x-2"
                  >
                    <span>{t('viewProfile')}</span>
                    <Icon name="ArrowRightIcon" size={16} variant="outline" />
                  </Link>
                </div>
              </article>
            ))}
          </div>

          {/* Empty State */}
          {filteredManufacturers.length === 0 && (
            <div className="text-center py-16">
              <Icon name="BuildingOfficeIcon" size={48} variant="outline" className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No manufacturers found</h3>
              <p className="text-muted-foreground">Try adjusting your search criteria or filters</p>
            </div>
          )}
        </section>
      </main>
    </>
  );
}
